/**
 * Formats a number representing followers count into a more readable format.
 *
 * @param value - The number of followers.
 * @param options - An optional object with a `lowercase` property to specify whether the result should be in lowercase.
 * @returns A string representing the formatted followers count.
 *
 * @example
 * formatFollowersCount(1000000) // returns "1.0M"
 * formatFollowersCount(1000000, { lowercase: true }) // returns "1.0m"
 * formatFollowersCount(1000000000, { lowercase: false }) // returns "1.0B"
 */
export function formatFollowersCount(
  value,
  options: { lowercase?: boolean } = {}
) {
  const { lowercase } = options;

  let result;
  if (value >= 1e9) {
    result = (value / 1e9).toFixed(1) + "B";
  } else if (value >= 1e6) {
    result = (value / 1e6).toFixed(1) + "M";
  } else if (value >= 1e3) {
    result = (value / 1e3).toFixed(1) + "K";
  } else {
    result = value?.toString();
  }

  return lowercase ? result.toLowerCase() : result;
}

/**
 * Parses a string representing a number with a suffix (e.g., "10K", "5M", "2B") and returns the numeric value.
 *
 * @param value - The string to parse. It should be a number followed by an optional suffix ("K", "M", "B").
 * @returns The numeric value of the input string. If the input string does not have a valid suffix, it returns the parsed number as is.
 *
 * @example
 * parseStringToNumbers("10K") // returns 10000
 * parseStringToNumbers("5M") // returns 5000000
 * parseStringToNumbers("2B") // returns 2000000000
 * parseStringToNumbers("100") // returns 100
 * parseStringToNumbers("") // returns 0
 */
export function parseStringToNumbers(value: string): number {
  if (!value) return 0;

  const suffix = value.slice(-1); // Get the last character
  const numberPart = parseFloat(value.slice(0, -1)); // Get the numeric part

  switch (suffix.toUpperCase()) {
    case "K":
      return numberPart * 1e3; // Thousands
    case "M":
      return numberPart * 1e6; // Millions
    case "B":
      return numberPart * 1e9; // Billions
    default:
      return parseFloat(value); // If no suffix, return the number as is
  }
}

export function numberToWords(num) {
  if (num === 0) return "zero";

  const belowTwenty = [
    "Zero",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  // const thousands = ["", "Thousand", "Lakh", "Crore"];

  function helper(n) {
    if (n < 20) return belowTwenty[n];
    if (n < 100)
      return (
        tens[Math.floor(n / 10)] + (n % 10 ? " " + belowTwenty[n % 10] : "")
      );
    if (n < 1000)
      return (
        belowTwenty[Math.floor(n / 100)] +
        " hundred" +
        (n % 100 ? " " + helper(n % 100) : "")
      );
    if (n < 100000)
      // less than 1 lakh
      return (
        helper(Math.floor(n / 1000)) +
        " thousand" +
        (n % 1000 ? " " + helper(n % 1000) : "")
      );
    if (n < 10000000)
      // less than 1 crore
      return (
        helper(Math.floor(n / 100000)) +
        " lakh" +
        (n % 100000 ? " " + helper(n % 100000) : "")
      );
    return (
      helper(Math.floor(n / 10000000)) +
      " crore" +
      (n % 10000000 ? " " + helper(n % 10000000) : "")
    );
  }

  return helper(num);
}

enum NumberFormat {
  International = "international",
  Indian = "indian",
  National = "national",
}
interface FormatNumberWithCommasOptions {
  num: number | string;
  format?: NumberFormat;
}

export function formatNumberWithCommas({
  num,
  format = NumberFormat.International,
}: FormatNumberWithCommasOptions) {
  const numStr = num.toString();

  if (format === NumberFormat.International) {
    return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  } else if (
    format === NumberFormat.Indian ||
    format === NumberFormat.National
  ) {
    return numStr
      .replace(/(\d+)(\d{3})(?=\d{2})/, "$1,$2")
      .replace(/\B(?=(\d{2})+(?!\d))/g, ",");
  } else {
    throw new Error(
      'Invalid format specified. Use "international" or "indian".'
    );
  }
}

// converts a decimal representation of time (e.g., 0.8, 1.2, 4) into years and months
export function convertToYearsMonths(time) {
  const years = Math.floor(time); // Whole years
  const months = Math.round((time - years) * 12); // Convert decimal part to months

  let result = "";

  if (years > 0) {
    result += `${years} year${years > 1 ? "s" : ""}`; // Pluralize 'year' if necessary
  }

  if (months > 0) {
    if (result) result += ", "; // Add a comma if there are both years and months
    result += `${months} month${months > 1 ? "s" : ""}`; // Pluralize 'month' if necessary
  }

  if (!years && !months) {
    result = "0 months"; // Handle the edge case where time is exactly 0
  }

  return result;
}

export function convertToYearsMonthsBracket(time: number): string {
  const years = time; // Whole years

  if (years >= 0 && years < 3) {
    return "0-3 years";
  } else if (years >= 3 && years < 5) {
    return "3-5 years";
  } else if (years >= 5 && years < 10) {
    return "5-10 years";
  } else if (years >= 10) {
    return "More than 10 years";
  }

  return "0-3 years"; // Default fallback for edge cases (e.g., negative values)
}

export function getLkdPostUrn(postUrl) {
  // Check for null, undefined, or empty input
  if (!postUrl) {
    return null;
  }

  try {
    // Remove query parameters
    postUrl = postUrl.split("?")[0];

    const urnRegex = /urn:li:activity:(\d+)/;
    const match = postUrl.match(urnRegex);

    if (match && match[1]) {
      return match[1];
    }

    // Validate URL format
    let url;
    try {
      url = new URL(postUrl);
    } catch (error) {
      return null;
    }

    const pathParts = url.pathname.split("/").filter(Boolean);

    // Handle different URL formats
    if (pathParts.includes("activity")) {
      const activityIndex = pathParts.indexOf("activity");
      return pathParts[activityIndex + 1] || null;
    }

    // Handle URL format with activity-{id}
    const activityIndex = pathParts.findIndex((part) =>
      part.startsWith("activity-")
    );

    if (activityIndex !== -1) {
      const activityPart = pathParts[activityIndex];
      const activityId = activityPart.split("-")[1];
      return activityId || null;
    }

    // Handle URL format with activity-{id} in last part
    const lastPart = pathParts[pathParts.length - 1];
    const activityIdMatch = lastPart.match(/activity-(\d+)/);
    if (activityIdMatch && activityIdMatch[1]) {
      return activityIdMatch[1];
    }

    return null;
  } catch (error) {
    // Catch any unexpected errors
    return null;
  }
}

export function calculatePostDate(postUrl) {
  const urn = getLkdPostUrn(postUrl);
  if (!urn) {
    // console.error("Invalid LinkedIn post URL");
    return null;
  }
  const binaryNumber = BigInt(urn)?.toString(2)?.padStart(64, "0");
  const first41BinaryDigits = binaryNumber?.slice(0, 42);
  const decimalInteger = parseInt(first41BinaryDigits, 2);
  const postDate = new Date(decimalInteger);
  return postDate;
}
